import React, { useState, useEffect, useCallback } from 'react';
import {
  getBaseRequestsForOperator,
  approveBaseRequest,
  rejectBaseRequest,
} from 'service/DriverRegistrationService';
import Notification from 'utils/Notifications';

const TYPE_LABELS = { join: 'Вступление', leave: 'Смена базы' };

const BaseRequestsOperatorPanel = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBaseRequestsForOperator();
      setRecords(res.data?.results || res.data || []);
    } catch (e) {
      console.error('Failed to load base requests for operator', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      await approveBaseRequest(id);
      setNotify({ isOpen: true, message: 'Заявка одобрена', type: 'success' });
      setExpandedId(null);
      await load();
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || 'Ошибка', type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      setNotify({ isOpen: true, message: 'Укажите причину', type: 'warning' });
      return;
    }
    setActionLoading(true);
    try {
      await rejectBaseRequest(rejectModal.id, rejectReason);
      setNotify({ isOpen: true, message: 'Заявка отклонена', type: 'success' });
      setRejectModal(null);
      setRejectReason('');
      setExpandedId(null);
      await load();
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || 'Ошибка', type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  if (!loading && records.length === 0) return null;

  return (
    <div style={{ margin: '24px 0', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>

      {/* Panel header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
           Заявки водителей
          {records.length > 0 && (
            <span style={{ background: '#f59e0b', color: '#fff', borderRadius: 20, padding: '2px 9px', fontSize: 12, fontWeight: 700 }}>
              {records.length}
            </span>
          )}
        </h2>
        <button onClick={load} disabled={loading}
          style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 13, color: '#374151', fontWeight: 500 }}>
          {loading ? '...' : ' Обновить'}
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '28px 20px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Загрузка...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {records.map((br, idx) => (
            <div key={br.id} style={{ borderBottom: idx < records.length - 1 ? '1px solid #f3f4f6' : 'none' }}>

              {/* Summary row */}
              <div
                style={{ display: 'flex', alignItems: 'center', padding: '14px 20px', gap: 14, cursor: 'pointer', background: expandedId === br.id ? '#f9fafb' : '#fff' }}
                onClick={() => setExpandedId(expandedId === br.id ? null : br.id)}
              >
                <div style={{ width: 44, height: 44, borderRadius: 12, background: br.request_type === 'join' ? '#eff6ff' : '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                  {br.request_type === 'join' ? '' : ''}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {br.driver_name || ''}
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {br.driver_vehicle && <span> {br.driver_vehicle}</span>}
                    {br.driver_phone && <span style={{ marginLeft: 8 }}> {br.driver_phone}</span>}
                  </p>
                </div>
                <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, flexShrink: 0, background: br.request_type === 'join' ? '#dbeafe' : '#fff7ed', color: br.request_type === 'join' ? '#1d4ed8' : '#c2410c', whiteSpace: 'nowrap' }}>
                  {TYPE_LABELS[br.request_type] || br.request_type}
                </span>
                <span style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0, marginLeft: 4 }}>
                  {expandedId === br.id ? '' : ''}
                </span>
              </div>

              {/* Expanded detail */}
              {expandedId === br.id && (
                <div style={{ padding: '14px 20px 20px', background: '#fafafa', borderTop: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px 16px', marginBottom: 16 }}>
                    {[
                      ['Водитель', br.driver_name],
                      ['Телефон', br.driver_phone || ''],
                      ['Гос. номер', br.driver_vehicle || ''],
                      ['Статус', br.driver_status || ''],
                      ['Тип заявки', TYPE_LABELS[br.request_type] || ''],
                      br.new_base_name ? ['Желаемая база', br.new_base_name] : null,
                    ].filter(Boolean).map(([label, val]) => (
                      <div key={label}>
                        <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 13, fontWeight: 600, color: '#111827' }}>{val}</p>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button onClick={() => handleApprove(br.id)} disabled={actionLoading}
                      style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#15803d,#16a34a)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, boxShadow: '0 2px 8px rgba(21,128,61,0.25)' }}>
                       Одобрить
                    </button>
                    <button onClick={() => { setRejectModal({ id: br.id, name: br.driver_name }); setRejectReason(''); }} disabled={actionLoading}
                      style={{ padding: '9px 22px', borderRadius: 9, border: '1.5px solid #dc2626', background: '#fff', color: '#dc2626', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                       Отклонить
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reject bottom-sheet */}
      {rejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 9999 }}
          onClick={e => e.target === e.currentTarget && setRejectModal(null)}>
          <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', padding: '24px 20px 36px', width: '100%', maxWidth: 520, boxShadow: '0 -4px 30px rgba(0,0,0,0.15)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700 }}>Отклонить заявку</h3>
            <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: 13 }}>{rejectModal.name}</p>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              placeholder="Укажите причину отказа..."
              rows={4} autoFocus
              style={{ width: '100%', borderRadius: 10, border: '1.5px solid #d1d5db', padding: '10px 13px', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setRejectModal(null)}
                style={{ padding: '9px 22px', borderRadius: 9, border: '1.5px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                Отмена
              </button>
              <button onClick={handleRejectSubmit} disabled={actionLoading}
                style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                {actionLoading ? 'Отправка...' : 'Отклонить'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
};

export default BaseRequestsOperatorPanel;
